import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChartBar, CaretDown, CaretUp } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MyMonthlyReportsPage() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/my/monthly-reports`);
        setReports(Array.isArray(data) ? data : []);
        if (Array.isArray(data) && data.length > 0) setOpenId(data[0].id);
      } catch {
        toast.error('Erro a carregar relatórios');
      } finally { setLoading(false); }
    })();
  }, []);

  const fmtMonth = (ym) => {
    const [y, m] = (ym || '').split('-');
    const names = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
    return `${names[parseInt(m) - 1] || ''} ${y}`;
  };

  return (
    <div className="fade-in max-w-xl mx-auto" data-testid="my-monthly-reports-page">
      <h1 className="font-heading text-2xl text-brand-text mb-1">Os meus relatórios mensais</h1>
      <p className="text-xs text-brand-muted mb-5">Recebes este resumo automaticamente no primeiro dia de cada mês.</p>

      {loading ? (
        <p className="text-xs text-brand-muted text-center py-10">A carregar...</p>
      ) : reports.length === 0 ? (
        <div className="bg-white border border-brand-border rounded-2xl p-8 text-center" data-testid="reports-empty">
          <ChartBar size={32} className="mx-auto text-brand-placeholder mb-2" weight="duotone" />
          <p className="text-xs text-brand-muted">Ainda não há relatórios. O primeiro chega no início do próximo mês.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {reports.map((r) => {
            const open = openId === r.id;
            const wDelta = (r.weight_first_kg != null && r.weight_last_kg != null) ? (r.weight_last_kg - r.weight_first_kg).toFixed(1) : null;
            const wSign = wDelta != null && wDelta > 0 ? '+' : '';
            return (
              <li key={r.id} className="bg-white border border-brand-border rounded-2xl overflow-hidden" data-testid={`report-${r.year_month}`}>
                <button
                  onClick={() => setOpenId(open ? null : r.id)}
                  className="w-full flex items-center gap-3 p-3 text-left"
                  data-testid={`report-toggle-${r.year_month}`}
                >
                  <div className="w-9 h-9 rounded-full bg-brand-gold/15 flex items-center justify-center flex-shrink-0">
                    <ChartBar size={14} className="text-brand-gold-hover" weight="duotone" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-brand-text">{fmtMonth(r.year_month)}</p>
                    <p className="text-[10px] text-brand-muted">{r.days_with_diary}/{r.days_in_month} dias com diário · {r.checkins_count} check-ins</p>
                  </div>
                  {open ? <CaretUp size={14} /> : <CaretDown size={14} />}
                </button>
                {open && (
                  <div className="border-t border-brand-border p-4 space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-brand-muted">Diários preenchidos</span><span className="font-medium text-brand-text">{r.days_with_diary} / {r.days_in_month}</span></div>
                    <div className="flex justify-between"><span className="text-brand-muted">Saciedade média</span><span className="font-medium text-brand-text">{r.satiety_avg != null ? `${r.satiety_avg}/5` : 'sem dados'}</span></div>
                    <div className="flex justify-between"><span className="text-brand-muted">Peso (início → fim)</span><span className="font-medium text-brand-text">{r.weight_first_kg ?? '-'} → {r.weight_last_kg ?? '-'} kg {wDelta != null ? `(${wSign}${wDelta} kg)` : ''}</span></div>
                    <div className="flex justify-between"><span className="text-brand-muted">Check-ins</span><span className="font-medium text-brand-text">{r.checkins_count}</span></div>
                    {r.motivational_message && (
                      <div className="bg-brand-gold/10 rounded-xl p-3 mt-3" data-testid={`report-motivational-${r.year_month}`}>
                        <p className="text-[10px] uppercase tracking-wide text-brand-gold-hover mb-1">Mensagem da nutricionista</p>
                        <p className="text-sm text-brand-text italic">"{r.motivational_message}"</p>
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
